<template>
  <div
    ref="pictureContainer"
    :class="[
      'picture-container',
      {
        'lazy-load-placeholder':
          showSkeleton && Object.keys(skeletonOptions)?.length,
      },
    ]"
    :style="[pictureContainerStyles]"
  >
    <picture
      v-if="isValidImageUrl"
      :class="[{ 'picture-position': showSkeleton }]"
    >
      <template v-if="responsive">
        <template
          v-for="(media, breakpointName) in mediaMapByBreakPoints"
          :key="breakpointName"
        >
          <template
            v-for="imgType in imgTypes"
            :key="imgType"
          >
            <source
              :media="media"
              :type="getSrcType(imgType)"
              :srcset="
                getSrcSet({
                  breakpointName: breakpointName,
                  imageType: imgType,
                  isRetina: retinaAdjustment,
                })
              "
            >
          </template>
        </template>
      </template>
      <template v-else>
        <template v-if="retinaAdjustment">
          <source
            media="(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)"
            type="image/webp"
            :srcset="
              getSrcSet({ imageType: 'webp', isRetina: retinaAdjustment })
            "
          >
          <source
            type="image/webp"
            :srcset="getSrcSet({ imageType: 'webp' })"
          >
        </template>
        <template v-else>
          <template
            v-for="imgType in imgTypes"
            :key="imgType"
          >
            <source
              :type="getSrcType(imgType)"
              :srcset="getSrcSet({ imageType: imgType })"
            >
          </template>
        </template>
      </template>
      <img
        v-if="!hasError"
        ref="imageRef"
        :data-src="`${getImgFullUrl()}`"
        :src="imgSrc()"
        :alt="!showSkeleton ? imageMap?.alt || alt : null"
        :class="['picture-image', { loaded: isLoaded }]"
        :style="[imageStyles]"
        :loading="loadingAttr"
        :usemap="imageMap ? `#map${imageMap.id}` : undefined"
        :fetchpriority="currentFetchPriority"
        :draggable="draggable"
        :width="imageIntrinsicWidth ? Math.round(imageIntrinsicWidth) : 'auto'"
        :height="
          imageIntrinsicWidth
            ? Math.round(imageIntrinsicWidth / aspectRatio)
            : 'auto'
        "
        v-bind="pt['img']"
        @load="onImageLoaded"
        @error="onImageUrlError"
      >
    </picture>
    <div
      v-if="!isValidImageUrl || hasError"
      role="img"
      :alt="imageMap?.alt || alt || 'fallback image'"
      :aria-label="imageMap?.alt || alt || 'fallback image'"
      :style="[fallbackStyles]"
      class="grid-x picture-image picture-fallback-background"
    >
      <div class="picture-fallback-content">
        <Icon :icon="csgPicture" />
        <div
          v-if="fallbackPicText !== ''"
          class="picture-fallback-text"
        >
          {{ fallbackPicText }}
        </div>
      </div>
    </div>
    <Skeleton
      v-if="showSkeleton && !hasError"
      :class="[enableSkeletonAnimation ? '' : 'remove-skeleton-animation']"
      :width="skeletonOptions.width ? skeletonOptions.width : '100%'"
      :height="
        skeletonOptions.height ? skeletonOptions.height : `${containerHeight}px`
      "
      :padding-bottom="
        skeletonOptions.paddingBottom ? skeletonOptions.paddingBottom : '0px'
      "
    />
  </div>
  <map
    v-if="imageMap?.children?.length"
    :id="`map${imageMap.id}`"
    :name="`map${imageMap.id}`"
  >
    <area
      v-for="(item, index) in imageMap?.children"
      :key="index"
      :href="item.url"
      :alt="item.alt"
      :coords="getCoords(item.coordinates, imageMap?.width)"
      @click="onImageClick(item)"
    >
  </map>
</template>

<script setup>
import {
  watch,
  ref,
  toRef,
  toRefs,
  onMounted,
  getCurrentInstance,
} from 'vue';
import { csgPicture } from '@csgSvgIcons/icons';
import { getMediaMap } from './media';
import useImageMap from './useImageMap';
import useStyles from './useStyles';
import useImageSrc from './useImageSrc';
import useImageSetup from './useImageSetup';
import useImageValidation from './useImageValidation';
import Skeleton from '../skeleton';
import Icon from '../icon';
import { SCENE7_CMDS, DEFAULT_SCENE7_MODIFIERS } from './constants';

const props = defineProps({
  pt: {
    type: Object,
    default: () => ({}),
  },
  nativeLazy: {
    type: [Boolean],
    default: false,
  },
  // #region props that should DEPRECATED in favor of using pt
  draggable: {
    type: String,
    default: 'auto',
    validator: (val) => ['true', 'false', 'auto'].includes(val),
  },
  fetchPriority: {
    type: [Boolean, String],
    default: false,
    validator: (val) => [true, false, 'high', 'low', 'auto'].includes(val),
  },
  lazyLoad: {
    type: Boolean,
    default: true,
    validator: (val) => [true, false].includes(val),
  },
  // #endregion
  imageUrl: {
    type: String,
    default: '',
  },
  imageIntrinsicWidth: {
    type: Number,
    default: null,
  },
  alt: {
    type: String,
    default: '',
  },
  imageSizesByBreakpoints: {
    type: Object,
    default: () => ({}),
  },
  imageModifiers: {
    type: Object,
    default: () => DEFAULT_SCENE7_MODIFIERS,
    // check if cmd exists in SCENE7 or is a custom variable matching $<string>$ pattern
    validator: (obj) => Object.keys(obj).every(
      (cmd) => SCENE7_CMDS.includes(cmd) || cmd.match(/(^\$+[\w]*.+\$$)|(^\$\$$)/),
    ),
  },
  usingScene7: {
    type: Boolean,
    default: true,
  },
  imageUrlsByBreakpoints: {
    type: Object,
    default: () => ({}),
  },
  imageType: {
    type: String,
    default: '',
  },
  imageMap: {
    type: Object,
    default: () => undefined,
  },
  skeleton: {
    type: Boolean,
    default: false,
  },
  aspectRatio: {
    type: Number,
    default: 0.8,
  },
  setScene7Hei: {
    type: Boolean,
    default: false,
  },
  filter: {
    type: String,
    default: null,
  },
  opacity: {
    type: Number,
    default: null,
  },
  skeletonOptions: {
    type: Object,
    default: () => ({}),
  },
  fallbackPicText: {
    type: String,
    default: null,
  },
  retinaAdjustment: {
    type: Boolean,
    default: false,
    required: false,
  },
  retinaQuality: {
    type: String,
    default: '',
    required: false,
  },
  preset: {
    type: Boolean,
    default: false,
    required: false,
  },
});
const emit = defineEmits(['imageClick']);

// #region Lifecycle
const {
  enableSkeletonAnimation,
  showSkeleton,
  pictureContainer,
  containerHeight,
  lazyLoaded,
  currentFetchPriority,
  loadingAttr,
} = useImageSetup(toRefs(props));
// #endregion

// #region error handling, image validation
const { hasError, onImageUrlError, isValidImageUrl } = useImageValidation({
  imageUrl: toRef(props, 'imageUrl'),
});

if (!isValidImageUrl.value) {
  // prevent skeleton loader when url is an empty string or invalid
  showSkeleton.value = false;
}
// #endregion

// Prop deprecated message should be removed once they are no longer used.
if (process.env.NODE_ENV === 'development') {
  onMounted(() => {
    const _vueInstance = getCurrentInstance();
    if (typeof _vueInstance.vnode.props.draggable !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[Atomic UI warn]: draggable will be deprecated in favor of using pt. 
<Picture :pt="{
  img: {
    'draggable': <true | false | auto>
  }
}" />`);
    }
    if (typeof _vueInstance.vnode.props['fetch-priority'] !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[Atomic UI warn]: fetchPriority will be deprecated in favor of using pt. 
<Picture :pt="{
  img: {
    'fetchPriority': <high | low | auto>
  }
}" />`);
    }
    if (typeof _vueInstance.vnode.props['lazy-load'] !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(`[Atomic UI warn]: lazyLoad will be deprecated in favor of using pt. 
<Picture :pt="{
  img: {
    'loading': <eager | lazy>
  }
}" />`);
    }
  });
}
// #endregion

// #region scene7 and non-scene7 image
// passing the whole props instead of only necessary parameters
const {
  getImgFullUrl, getSrcSet, responsive, imgSrc,
} = useImageSrc({
  ...toRefs(props),
  lazyLoaded,
});
// #endregion

// #region image related
const mediaMapByBreakPoints = props.usingScene7 || Object.keys(props.imageUrlsByBreakpoints).length
  ? getMediaMap()
  : {};
const isLoaded = ref(false);
// use imageType from props if available
// In all other cases, we have default source set supported as jpeg and webp
const imgTypes = props.imageType ? [props.imageType] : ['webp', 'jpeg'];
const getSrcType = (imageType) => (imageType === 'webp-alpha' ? 'image/webp' : `image/${imageType}`);

const onImageLoaded = async (event) => {
  // In CSR, transparent background in img src will trigger @load event.
  // Skeleton should only be removed for actual img src, not transparent background.
  if (event.target?.src?.indexOf('data:image/svg+xml') < 0) {
    showSkeleton.value = false;
    isLoaded.value = true;
  }
};

const imageRef = ref(null);
const stop = watch(imageRef, (element) => {
  // When using loading=lazy in Chrome and disable cache unchecked,
  // memory cached image will not trigger @load event.
  if (element?.complete && element?.src !== '') {
    showSkeleton.value = false;
    isLoaded.value = true;
    stop();
  }
});
// #endregion

// #region ImageMap
const { getCoords } = useImageMap(imageRef);

const onImageClick = (imageData = {}) => {
  emit('imageClick', imageData);
};
// #endregion

// #region stylings
const { pictureContainerStyles, imageStyles, fallbackStyles } = useStyles({
  opacity: toRef(props, 'opacity'),
  filter: toRef(props, 'filter'),
  showSkeleton,
  skeletonOptions: toRef(props, 'skeletonOptions'),
  imageIntrinsicWidth: toRef(props, 'imageIntrinsicWidth'),
  aspectRatio: toRef(props, 'aspectRatio'),
});
// #endregion
</script>

<style lang="scss" scoped>
.picture-container {
  position: relative;

  // IntersectionObserver fix for scaled displays
  & > picture {
    min-height: 1px;
    min-width: 1px;
  }

  .picture-position {
    position: absolute;
  }
}

.picture-image {
  width: 100%;
}

.picture-fallback-background {
  background: $hover-color;
  position: relative;
}

:deep(.picture-fallback-content) {
  margin: auto;
}

:deep(.picture-fallback-content) {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  text-align: center;
}

.picture-fallback-text {
  font-weight: $font-weight-sm;
  font-size: $font-size-sm;
  line-height: $line-height-sm;
  text-align: center;
}

.lazy-load-placeholder {
  height: 0px;
  position: relative;
  width: 100%;

  img {
    display: block;
    min-height: 1px;
    position: absolute;
    visibility: hidden;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    transition: opacity 0.15s ease-in;
    opacity: 0;
    width: 100%;

    &.loaded {
      visibility: visible;
      opacity: 1;
    }
  }
}

.remove-skeleton-animation {
  animation: none;
  background-color: $hover-color;
}
</style>

<style lang="scss" brand="mcom" scoped>
</style>

<style lang="scss" brand="bcom" scoped>
</style>
